import React from 'react';
import dayjs from 'dayjs';
import { useTheme } from '@theme/useTheme';
import CircularProgress from '@components/ui/CircularProgress';

interface AssessmentData {
  risk_score: number;
  risk_level: string;
  taken_at: Date;
}

interface AssessmentCardProps {
  data: AssessmentData;
  bgColor: string;
  setBgColor: (bgColor: string) => void;
}

const AssessmentCard: React.FC<AssessmentCardProps> = ({
  data,
  bgColor,
  setBgColor,
}) => {
  const { colors } = useTheme();
  // Responsive size for circular progress
  const getProgressSize = () => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 640 ? 180 : 200; // Smaller on mobile
    }
    return 200;
  };

  const getFontSize = () => {
    if (typeof window !== 'undefined') {
      return window.innerWidth < 640 ? 40 : 45; // Smaller font on mobile
    }
    return 45;
  };

  // Fallback in case the back-end doesn't send taken_at
  const takenAt = data?.taken_at
    ? dayjs(data.taken_at).format("DD MMM YYYY • HH:mm")
    : "Unknown date";

  return (
    <div
      className="flex flex-col items-center rounded-2xl px-6 py-6 sm:px-10 sm:py-7 mt-1 shadow-lg border relative"
      style={{
        backgroundColor: colors.background,
        borderColor: colors.border
      }}
    >
      {/* Gradient header strip */}
      <div
        className="absolute top-0 left-1 right-1 h-1.5 rounded-t-2xl"
        style={{
          background: `linear-gradient(to right, ${colors.primary}, ${colors.primaryButton})`
        }}
      ></div>

      <h2
        className="text-xl sm:text-2xl font-bold mt-1.5 mb-1 text-center"
        style={{ color: colors.heading }}
      >
        Latest Assessment Results
      </h2>
      <p
        className="text-sm mb-4 text-center"
        style={{ color: colors.text }}
      >
        Taken on {takenAt}
      </p>

      {/* Circular score */}
      <CircularProgress
        percentage={data.risk_score}
        setBgColor={setBgColor}
        size={getProgressSize()}
        fontSize={getFontSize()}
      />

      <p
        className="text-center mt-4 text-xl sm:text-2xl font-bold"
        style={{ color: colors.heading }}
      >
        Risk Level
      </p>

      {/* Risk badge */}
      <div
        className="mt-4 py-2 px-4 sm:px-6 rounded-2xl"
        style={{ backgroundColor: bgColor }}
      >
        <span className="text-white font-semibold text-lg sm:text-xl tracking-wide">
          {data.risk_level}
        </span>
      </div>
    </div>
  );
};

export default AssessmentCard;
